import { useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useGetActiveBannersQuery } from '@/redux/banner/banner.api.slice';

export default function BannerCarousel() {
  const { data: banners = [] } = useGetActiveBannersQuery();

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  // ponytail: stopOnInteraction covers the practical WCAG bar; add an explicit pause button if an audit demands one
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true },
    prefersReducedMotion
      ? []
      : [Autoplay({ delay: 5000, stopOnInteraction: true })]
  );

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi]
  );

  if (banners.length === 0) return null;

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="overflow-hidden rounded-lg" ref={emblaRef}>
        <div className="flex">
          {banners.map((banner) => {
            const inner = (
              <>
                {/* Mobile image (shown on small screens, fallback to desktop) */}
                <img
                  src={banner.mobileUrl ?? banner.desktopUrl}
                  alt="Banner"
                  className="block md:hidden w-full h-auto"
                />
                {/* Desktop image */}
                <img
                  src={banner.desktopUrl}
                  alt="Banner"
                  className="hidden md:block w-full h-auto"
                />
              </>
            );

            return (
              <div key={banner._id} className="flex-[0_0_100%] min-w-0">
                {banner.link ? (
                  <a
                    href={banner.link}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                  >
                    {inner}
                  </a>
                ) : (
                  <div>{inner}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots navigation — only shown if more than one banner */}
      {banners.length > 1 && (
        <div className="flex justify-center gap-2 mt-2">
          {banners.map((_, i) => (
            <button
              type="button"
              key={i}
              onClick={() => scrollTo(i)}
              className="w-2 h-2 rounded-full bg-muted-foreground/40 hover:bg-muted-foreground/70 transition-colors"
              aria-label={`Go to banner ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
